import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/useToast'
import { authApi } from '@/lib/api'
import { AxiosError } from 'axios'

interface ApiErrorResponse {
  success: boolean
  error?: {
    code?: string
    message?: string
  }
}

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const { login, sendMagicLink } = useAuth()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [emailNotVerified, setEmailNotVerified] = useState(false)
  const [resendingVerification, setResendingVerification] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setEmailNotVerified(false)
    try {
      await login(data.email, data.password)
      navigate('/feed')
    } catch (error) {
      // Handle both enhanced errors (from interceptor) and original AxiosError format
      const enhancedError = error as Error & { code?: string; originalError?: AxiosError<ApiErrorResponse> }
      const axiosError = enhancedError.originalError || (error as AxiosError<ApiErrorResponse>)
      const apiError = axiosError.response?.data as ApiErrorResponse | undefined
      const errorCode = enhancedError.code || apiError?.error?.code
      const errorMessage = enhancedError.message || apiError?.error?.message

      if (errorCode === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(true)
        toast({
          title: 'Email not verified',
          description: 'Please check your inbox and verify your email before logging in.',
          variant: 'destructive',
        })
        return
      } else if (errorCode === 'INVALID_CREDENTIALS') {
        toast({
          title: 'Invalid credentials',
          description: 'The email or password you entered is incorrect.',
          variant: 'destructive',
        })
      } else if (errorCode === 'ACCOUNT_SUSPENDED') {
        toast({
          title: 'Account suspended',
          description: 'Your account has been suspended. Please contact support.',
          variant: 'destructive',
        })
      } else if (errorCode === 'PARENTAL_CONSENT_REQUIRED') {
        toast({
          title: 'Parental consent required',
          description: 'Your account requires parental consent before you can log in.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Login failed',
          description: errorMessage || 'Please check your email and password and try again.',
          variant: 'destructive',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendVerification = async () => {
    const email = getValues('email')
    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address.',
        variant: 'destructive',
      })
      return
    }

    setResendingVerification(true)
    try {
      // Use the magic link endpoint with verify_email purpose
      await authApi.sendMagicLink(email, 'verify_email')
      toast({
        title: 'Verification email sent!',
        description: 'Please check your inbox for the verification link.',
      })
      setEmailNotVerified(false)
    } catch {
      toast({
        title: 'Failed to send email',
        description: 'Please try again later.',
        variant: 'destructive',
      })
    } finally {
      setResendingVerification(false)
    }
  }

  const handleMagicLink = async () => {
    const email = getValues('email')
    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address first.',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      await sendMagicLink(email)
      setMagicLinkSent(true)
      toast({
        title: 'Magic link sent!',
        description: 'Check your email for the login link.',
      })
    } catch (error) {
      toast({
        title: 'Failed to send magic link',
        description: 'Please try again later.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className="border-0 shadow-none lg:border lg:shadow-sm">
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="font-display text-2xl">Welcome back</CardTitle>
        <CardDescription>Sign in to your KLASSIC account</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Social login buttons */}
        <div className="grid grid-cols-2 gap-4">
          <Button variant="outline" className="w-full" disabled>
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google
          </Button>
          <Button variant="outline" className="w-full" disabled>
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
              />
            </svg>
            Apple
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        {/* Email not verified warning */}
        {emailNotVerified && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-amber-800 dark:text-amber-200">
                  Email not verified
                </h4>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  Please verify your email address before logging in. Check your inbox for the verification link.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                >
                  {resendingVerification && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                  Resend verification email
                </Button>
              </div>
            </div>
          </div>
        )}

        {magicLinkSent ? (
          <div className="text-center py-4">
            <Mail className="mx-auto h-12 w-12 text-primary" />
            <h3 className="mt-4 font-medium">Check your email</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              We sent a magic link to your email. Click the link to sign in.
            </p>
            <Button
              variant="link"
              className="mt-4"
              onClick={() => setMagicLinkSent(false)}
            >
              Use a different method
            </Button>
          </div>
        ) : showMagicLink ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className="pl-9"
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            <Button
              className="w-full"
              onClick={handleMagicLink}
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Magic Link
            </Button>

            <Button
              variant="link"
              className="w-full"
              onClick={() => setShowMagicLink(false)}
            >
              Use password instead
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className="pl-9"
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium" htmlFor="password">
                  Password
                </label>
                <Button
                  variant="link"
                  className="h-auto p-0 text-xs"
                  type="button"
                  onClick={() => setShowMagicLink(true)}
                >
                  Use magic link instead
                </Button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-9"
                  {...register('password')}
                />
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
        )}
      </CardContent>

      <CardFooter className="flex flex-col space-y-2">
        <p className="text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="font-medium text-primary hover:underline">
            Sign up
          </Link>
        </p>
      </CardFooter>
    </Card>
  )
}
